import { AgGrid } from "@jsq/ds/core";
import { IconType } from "@jsq/ds/icons";
import { ComplianceCaseStatus, RiskRating } from "~/graphql/supergraphEnums";
import { timeDifferenceInMS } from "~/utils/datetime";
import { statusValueFormatter } from "../ComplianceTable/utils";
import { RISK_RATING_PROPS } from "../riskRating/constants";
import { RiskRatingCell } from "../riskRating/RiskRatingCell";
import { RiskRatingProps } from "../riskRating/types";
import { sortRiskRating } from "../riskRating/utils";
import { ActiveCasesCellRenderer } from "./ActiveCasesCellRenderer";
import { EscalationsCellRenderer } from "./EscalationsCellRenderer";
import OnboardingCellRenderer from "./OnboardingCellRenderer";
import PositionNameRenderer from "./PositionNameRenderer";
import ProgramRenderer from "./ProgramRenderer";
import { RootProfileCellRenderer } from "./RootProfileCellRenderer";
import { FillCell, ProgramColumn } from "./styles_vanilla.css";
import { PositionsTableRowItem } from "./types";

export enum ColumnField {
  Position = "position",
  Entity = "entity",
  Client = "client",
  Status = "status",
  Program = "program",
  RiskRating = "riskRating",
  InvestingAccount = "investingAccount",
  CreationDate = "date",
  OnboardingStatus = "onboardingStatus",
  RootProfile = "rootProfile",
  ActiveCases = "activeCases",
  Escalations = "escalations",
}

export const getColDefinitions = (annualRefreshEnabled: boolean): AgGrid.ColDef[] => {
  const riskRatingColumn: AgGrid.ColDef = {
    field: ColumnField.RiskRating,
    headerName: "Risk rating",
    cellRenderer: RiskRatingCell,
    cellRendererParams: ({ data }: { data: PositionsTableRowItem }) => {
      const cellProps: RiskRatingProps = RISK_RATING_PROPS[data.riskRating ?? RiskRating.None];
      return {
        hasIcon: cellProps.hasIcon,
        iconType: cellProps.iconType as IconType,
        color: cellProps.color,
        text: cellProps.text,
      };
    },
    filter: "agSetColumnFilter",
    filterParams: {
      values: Object.keys(RISK_RATING_PROPS),
    },
    comparator: sortRiskRating,
  };

  const columns: AgGrid.ColDef[] = [
    {
      field: ColumnField.Position,
      pinned: "left",
      headerName: "Position name",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
      },
      cellRenderer: PositionNameRenderer,
      cellRendererParams: ({ data }: { data: PositionsTableRowItem }) => ({
        account: data.position?.accountName,
        entity: data.position?.entityName,
      }),
      valueGetter: ({ data }: { data: PositionsTableRowItem }) => {
        return data.position?.accountName;
      },
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true,
      minWidth: 240,
    },
    {
      field: ColumnField.Entity,
      headerName: "Entity",
      filter: false,
      valueGetter: ({ data }: { data: PositionsTableRowItem }) => {
        return data.entity?.name ?? "";
      },
    },
    {
      field: ColumnField.Client,
      headerName: "Client",
      filter: false,
      valueGetter: ({ data }: { data: PositionsTableRowItem }) => {
        return data.client?.name ?? "";
      },
    },
    {
      field: ColumnField.InvestingAccount,
      headerName: "Investing profile",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
      },
    },
    {
      field: ColumnField.Status,
      headerName: "Status",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Active", "Completed"],
      },
      sortable: false,
    },
    {
      field: ColumnField.CreationDate,
      headerName: "Creation date",
      filter: "agDateColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        maxNumConditions: 1,
      },
      filterValueGetter: ({ data }: { data: PositionsTableRowItem }) => {
        return new Date(data.date);
      },
      comparator: timeDifferenceInMS,
      hide: true,
    },
    {
      field: ColumnField.Program,
      headerName: "Program",
      cellRenderer: ProgramRenderer,
      cellRendererParams: ({ data }: { data: PositionsTableRowItem }) => ({
        value: data.program,
      }),
      sortable: false,
      cellClass: ProgramColumn,
    },
    ...(annualRefreshEnabled
      ? [
          {
            field: ColumnField.ActiveCases,
            headerName: "Active cases",
            cellRenderer: ActiveCasesCellRenderer,
            filter: false,
            sortable: false,
            cellStyle: {
              display: "flex",
              alignItems: "center",
            },
          },
          {
            field: ColumnField.Escalations,
            headerName: "Escalations",
            cellRenderer: EscalationsCellRenderer,
            filter: false,
            sortable: false,
            cellStyle: {
              display: "flex",
              alignItems: "center",
            },
          },
          riskRatingColumn,
          {
            field: ColumnField.RootProfile,
            headerName: "Root profile",
            cellRenderer: RootProfileCellRenderer,
            filter: false,
            cellStyle: {
              display: "flex",
              alignItems: "center",
            },
            minWidth: 280,
          },
        ]
      : [riskRatingColumn]),
    {
      field: ColumnField.OnboardingStatus,
      headerName: "Onboarding status",
      cellRenderer: OnboardingCellRenderer,
      cellRendererParams: ({ data }: { data: PositionsTableRowItem }) => ({
        data: data,
      }),
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...Object.values(ComplianceCaseStatus), "None"],
        valueFormatter: (params: AgGrid.ValueFormatterParams): string => {
          return params.value == "None" ? "None" : statusValueFormatter(params);
        },
      },
      filterValueGetter: (params: AgGrid.ValueGetterParams<PositionsTableRowItem>): string => {
        const data = params.data;
        return data?.onboardingStatus?.status || "None";
      },
      valueGetter: (params: AgGrid.ValueGetterParams<PositionsTableRowItem>): string => {
        const data = params.data;
        if (data?.onboardingStatus) {
          return statusValueFormatter({
            value: data.onboardingStatus.status,
          } as AgGrid.ValueFormatterParams);
        } else {
          return "Action Required";
        }
      },
      cellClass: FillCell,
      minWidth: 300,
    },
  ];

  return columns;
};
